/**
 * Founder onboarding intake — the question registry (ClickUp `86d3uknw5`,
 * Onboarding 04).
 *
 * Single source of truth for the AI Onboarding Agent's conversation, following
 * the same philosophy as `playbook.ts`'s schema: the content is data, the
 * behaviour is a handful of pure functions over it, and both the API and the
 * dashboard read the same object rather than keeping two copies in step by
 * hand. `business-brief-schema.ts` is the equivalent for the LEGACY Typeform
 * brief; this file is the v3 intake and does not replace it.
 *
 * Transcribed from "Doneverse-AI-Onboarding-Agent-Spec v3.0" (Product & CX).
 * Every prompt, option list, nudge and threshold here is verbatim from that
 * document — if the spec changes, change it here, not at a call site.
 *
 * Deliberately NOT in this file:
 *   - Industry → `founder_categories` (A/C/E/L/M/P/R) mapping. The spec maps
 *     industry to a TRAINING STACK only; the category mapping is product
 *     decision #2d and is still open, so inventing one here would be a guess
 *     that later reads as a decision.
 *   - The NLP/LLM derivations (`contentPillars`, `autonomyLevel`, `aiTags`).
 *     Those are finalize-time analysis, not question content — Onboarding 05.
 */
import { z } from 'zod';
import { handleOrWebUrlSchema, webUrlSchema } from './web-urls.js';
// ── Section 1 — Business identity (10 questions, ~8 min) ─────────────
const SECTION_1 = [
    {
        // NOT in spec v3.0 — added on the product owner's instruction (2026-08-07),
        // because the business name exists nowhere else. Keap's standard `company`
        // field is empty on every founder we sampled and it has no custom field for
        // it, so if we do not ask, nothing downstream knows what the business is
        // called. `company` is also what the legacy business-brief validator
        // requires, so asking here is what lets a v3 brief satisfy it.
        //
        // Id is Q1.0, not Q1.11: it is an insertion, and a spec-shaped id would
        // collide the day v4 adds a real Q1.11.
        id: 'Q1.0',
        section: 1,
        inputType: 'text',
        fieldKey: 'company',
        prompt: 'First things first — what’s your business called?',
        minChars: 2,
        required: true,
        placeholder: 'RatedDocs',
    },
    {
        id: 'Q1.1',
        section: 1,
        inputType: 'voice',
        fieldKey: 'bizOverview',
        prompt: 'Tell me about your business in your own words — what do you do, who do you help, and why does it matter to you?',
        minWords: 60,
        required: true,
        coachingNudge: "Tell me a bit more — what changes for your clients after working with you? And what's the origin story — why did YOU start this business specifically? That story is one of your most powerful content assets.",
    },
    {
        id: 'Q1.2',
        section: 1,
        inputType: 'voice',
        fieldKey: 'icp',
        prompt: 'Describe your ideal client in detail. Go deep on the emotional and identity layer — not just what’s broken in their day, but how they feel about it and who they want to become. Include the content topics they care most about.',
        minWords: 80,
        required: true,
        coachingNudge: 'Think about your best-ever client. What did they worry about at 2am? What would they say on a bad day? And what did they look like 6 months after working with you?',
    },
    {
        id: 'Q1.3',
        section: 1,
        inputType: 'text',
        fieldKey: 'transformation',
        prompt: 'Complete this sentence: "I help [who] achieve [what outcome] in [timeframe] without [main pain point]."',
        minWords: 20,
        required: true,
        placeholder: 'Coaching & Consulting — Fractional CMO for enterprise businesses.',
        coachingNudge: 'Be specific about who, what, how long, and what they avoid.',
    },
    {
        id: 'Q1.4',
        section: 1,
        inputType: 'voice',
        fieldKey: 'competitiveAdvantage',
        prompt: 'Three things in one — your authority profile: (1) Name 2–3 biggest competitors. (2) Why do clients choose you over them. (3) What about your own background and experience gives you unique credibility in this space?',
        minWords: 30,
        required: true,
        coachingNudge: "What's in your background — qualifications, lived experience, career history — that makes you the right person to do this?",
    },
    {
        id: 'Q1.5',
        section: 1,
        inputType: 'text',
        fieldKey: 'industry',
        prompt: "What industry are you in, and what's your specific niche?",
        minChars: 10,
        required: true,
        placeholder: 'Coaching & Consulting — Fractional CMO for enterprise businesses.',
    },
    {
        id: 'Q1.6',
        section: 1,
        inputType: 'structured',
        fieldKey: 'services',
        prompt: 'What are your top 3 services or products? Include the price for each if you can.',
        required: true,
    },
    {
        id: 'Q1.7',
        section: 1,
        inputType: 'voice',
        fieldKey: 'customerAcquisition',
        prompt: 'Two quick ones about how you get customers. How did you get your very first customer? And how did you get your most recent one?',
        minWords: 30,
        required: true,
        coachingNudge: 'Even if it was informal — a friend, a referral, a social post — tell me the story.',
    },
    {
        id: 'Q1.8',
        section: 1,
        inputType: 'select',
        fieldKey: 'repeatRevenue',
        prompt: 'Roughly what percentage of your revenue comes from repeat customers and referrals?',
        required: true,
        options: [
            'Less than 20%',
            '20–40%',
            '40–60%',
            '60–80%',
            'More than 80%',
            "I don't track this yet",
        ],
    },
    {
        // NOT in spec v3.0 — added on the product owner's instruction (2026-08-07),
        // positioned after Q1.8 as she specified.
        //
        // The options are VERBATIM the brackets `deriveFounderCategory` already
        // keys on. They read a little oddly next to the other questions, and that
        // is deliberate: any friendlier wording would need an alias map, and a
        // mismatch there fails silently — the founder answers, the category never
        // derives, and nobody notices until a roadmap is missing.
        id: 'Q1.8a',
        section: 1,
        inputType: 'select',
        fieldKey: 'monthlyRevenue',
        prompt: 'Roughly what’s your monthly revenue right now?',
        required: true,
        options: [
            'Pre Revenue',
            '$1K - $2K / month',
            '$2K - $5K / month',
            '$5K - $8K / month',
            '$8K - $10K / month',
            '$10K - $20K / month',
            '$20K - $40K / month',
            '$40K - $60K / month',
            '$60K - $80K / month',
            '$80K - $100K / month',
            '$100K - $200K / month',
            '$200K - $500K / month',
            '$500K / month +',
        ],
    },
    {
        id: 'Q1.9',
        section: 1,
        inputType: 'url',
        fieldKey: 'websiteUrl',
        prompt: 'Drop your website URL and main social media links below.',
        required: true,
    },
    {
        id: 'Q1.10',
        section: 1,
        inputType: 'select',
        fieldKey: 'teamSize',
        prompt: 'How many people are currently on your team, not including your new Doer?',
        required: true,
        options: ['Just me', '1–5 people', '6–20 people', '20+'],
    },
];
// ── Section 2 — Brand & voice (5 questions, ~6 min) ──────────────────
const SECTION_2 = [
    {
        id: 'Q2.1',
        section: 2,
        inputType: 'voice',
        fieldKey: 'brandPersonality',
        prompt: 'How would you describe your brand personality? If your brand were a person at a dinner party, how would they show up? Give me 3–5 adjectives and a sentence.',
        minWords: 30,
        required: true,
        coachingNudge: 'Think about the tone of your best-performing post or email. What feeling did it create in the reader?',
    },
    {
        id: 'Q2.2',
        section: 2,
        inputType: 'voice',
        fieldKey: 'coreValues',
        prompt: 'What are your core values — the beliefs that drive how you run your business and show up for your clients? 3–5 values with a sentence on each.',
        minWords: 30,
        required: true,
        coachingNudge: "Think about a time you made a hard business decision. What guided it? That's usually a core value.",
    },
    {
        id: 'Q2.3',
        section: 2,
        inputType: 'structured',
        fieldKey: 'brandVoice',
        prompt: 'What words or phrases do you use all the time — and which ones would you never use?',
        required: true,
        coachingNudge: 'Even a couple of examples help.',
    },
    {
        id: 'Q2.4',
        section: 2,
        inputType: 'voice',
        fieldKey: 'notForWho',
        prompt: "Who is your offer NOT for? Describe the type of client you'd turn away or who tends to be a bad fit.",
        minWords: 30,
        required: true,
        coachingNudge: "Think about a client who didn't work out. What made them a bad fit?",
    },
    {
        id: 'Q2.5',
        section: 2,
        inputType: 'upload',
        fieldKey: 'brandAssets',
        prompt: 'Upload your brand assets — logo, style guide, any visual guidelines you have.',
        // "Skip option required" — the only question the spec explicitly allows a
        // founder to pass on. Skipping reveals the HEX + font fallback fields.
        required: false,
    },
];
// ── Section 3 — Priorities (8 questions, ~7 min) ─────────────────────
const SECTION_3 = [
    {
        id: 'Q3.1',
        section: 3,
        inputType: 'voice',
        fieldKey: 'whyDoer',
        prompt: "What made you invest in a Doer right now? What's the real problem you need solved?",
        minWords: 40,
        required: true,
        coachingNudge: "What's the one thing that, if your Doer sorted it in the next 30 days, would make this investment feel completely worth it?",
    },
    {
        id: 'Q3.2',
        section: 3,
        inputType: 'voice',
        fieldKey: 'previousAttempts',
        prompt: 'Before finding Doneverse, what had you already tried — freelancers, agencies, other VAs, DIY? What happened?',
        minWords: 30,
        required: true,
        coachingNudge: 'What specifically went wrong? Quality, communication, no system, cost?',
    },
    {
        id: 'Q3.3',
        section: 3,
        inputType: 'voice',
        fieldKey: 'thirtyDayWin',
        prompt: "What's the single project that would make your first 30 days a success — the one thing you'd genuinely celebrate?",
        minWords: 40,
        required: true,
        coachingNudge: 'The more specific — platforms, frequency, format — the better.',
    },
    {
        id: 'Q3.4',
        section: 3,
        inputType: 'voice',
        fieldKey: 'successMilestones',
        prompt: 'What does success look like at 3 months, 6 months, and 12 months? Be specific — revenue targets, client numbers, systems, content presence.',
        minWords: 40,
        required: true,
        coachingNudge: "Not just 'grow my business' — what concrete markers would make you say this year was a turning point?",
    },
    {
        id: 'Q3.5',
        section: 3,
        inputType: 'voice',
        fieldKey: 'leadFlow',
        prompt: 'Walk me through your current lead flow — what happens after someone first discovers you? Also: which lead sources have produced your highest-quality customers?',
        minWords: 30,
        required: true,
        coachingNudge: "'They find me on Instagram, DM me, then I send a Calendly link' is completely valid.",
    },
    {
        id: 'Q3.6',
        section: 3,
        inputType: 'text',
        fieldKey: 'objections',
        prompt: 'What objections do your prospects raise most often — the reasons people hesitate or decide not to work with you?',
        minChars: 10,
        required: true,
    },
    {
        id: 'Q3.7',
        section: 3,
        inputType: 'text',
        fieldKey: 'delegateTasks',
        prompt: 'What tasks do you currently do that you hate, avoid, or that eat your time? List anything your Doer should take off your plate.',
        minChars: 5,
        required: true,
    },
    {
        id: 'Q3.8',
        section: 3,
        inputType: 'select',
        fieldKey: 'marketingBudget',
        prompt: 'What is your approximate monthly marketing budget — money available for ads, tools, content creation, or other paid growth activities?',
        required: true,
        options: [
            '$0 — organic only',
            'Under $500/month',
            '$500–$1,500/month',
            '$1,500–$5,000/month',
            '$5,000–$10,000/month',
            '$10,000+/month',
        ],
    },
];
// ── Section 4 — Tech stack (4 questions, ~4 min) ─────────────────────
const SECTION_4 = [
    {
        id: 'Q4.1',
        section: 4,
        inputType: 'select',
        fieldKey: 'contentPlatforms',
        prompt: 'Which platforms do you post content on? Select all that apply.',
        required: true,
        multiSelect: true,
        options: [
            'Instagram',
            'TikTok',
            'Facebook',
            'YouTube',
            'LinkedIn',
            'Pinterest',
            'X/Twitter',
            'Threads',
            'Podcast',
            'Other',
            "I don't currently post",
        ],
    },
    {
        id: 'Q4.2',
        section: 4,
        inputType: 'industry',
        fieldKey: 'confirmedIndustry',
        prompt: 'Industry auto-detection + confirmation',
        required: true,
    },
    {
        id: 'Q4.3',
        section: 4,
        inputType: 'select',
        fieldKey: 'toolStack',
        // Options are NOT fixed: they come from the stack Q4.2 resolved to, per
        // category. See TRAINING_STACKS.
        prompt: 'Tool stack selection (driven by Q4.2 result)',
        required: true,
        multiSelect: true,
    },
    {
        id: 'Q4.4',
        section: 4,
        inputType: 'text',
        fieldKey: 'otherTools',
        prompt: "Are there any other tools critical to how your business runs that aren't in the list above?",
        required: true,
    },
];
// ── Section 5 — Content (8 questions, ~6 min) ────────────────────────
/** The exact answer that closes down the on-camera branch. */
export const OFF_CAMERA_ANSWER = 'I prefer to stay off camera';
/** The exact answer that closes down the AI-tools follow-up. */
export const NOT_USING_AI_ANSWER = 'Not using it yet';
const SECTION_5 = [
    {
        id: 'Q5.1',
        section: 5,
        inputType: 'voice',
        fieldKey: 'contentSituation',
        prompt: 'Walk me through your current content situation — how often you post, what formats you use, what’s working and what’s not.',
        minWords: 40,
        required: true,
        coachingNudge: 'Tell me about a piece of content that really resonated. What was it and why do you think it worked?',
    },
    {
        id: 'Q5.2',
        section: 5,
        inputType: 'voice',
        fieldKey: 'hooksAndTaglines',
        prompt: 'What taglines, hooks, or phrases have performed best? Also: what video content styles do you want to focus on — educational talking head, entertainment, interview style, skits, or something else?',
        minWords: 60,
        required: true,
        coachingNudge: "What's a line or concept that got a reaction? And what video energy feels most natural to your brand?",
    },
    {
        id: 'Q5.3',
        section: 5,
        inputType: 'select',
        fieldKey: 'contentFormats',
        prompt: 'What content formats do you want your Doer creating? Select all that apply.',
        required: true,
        multiSelect: true,
        options: [
            'Short-form video scripts',
            'Reels/TikTok captions',
            'Static post captions',
            'Carousels/graphics',
            'Blog posts',
            'Email newsletters',
            'YouTube scripts',
            'Podcast outlines',
            'Stories content',
        ],
    },
    {
        id: 'Q5.4',
        section: 5,
        inputType: 'select',
        fieldKey: 'contentTimeAvail',
        prompt: 'How many hours a week can you realistically give to content creation — filming, reviewing drafts, giving approvals?',
        required: true,
        options: [
            'Less than 1 hour',
            '1 to 3 hours',
            '4 to 6 hours',
            'I want to be as hands-off as possible',
        ],
    },
    {
        id: 'Q5.5',
        section: 5,
        inputType: 'select',
        fieldKey: 'onCamera',
        prompt: 'Are you comfortable being the face of your brand on camera?',
        required: true,
        options: [
            'Yes — I love it',
            'I can do it but need encouragement',
            OFF_CAMERA_ANSWER,
            "I'm open to trying",
        ],
    },
    {
        id: 'Q5.6',
        section: 5,
        inputType: 'select',
        fieldKey: 'filmingCommit',
        prompt: 'Can you commit to filming regularly — even just on your phone?',
        required: true,
        options: [
            'Yes — weekly is doable',
            'Maybe once or twice a month',
            'Not right now but open to it later',
        ],
        skipWhen: { questionId: 'Q5.5', equals: [OFF_CAMERA_ANSWER] },
    },
    {
        id: 'Q5.7',
        section: 5,
        inputType: 'select',
        fieldKey: 'filmingSetup',
        prompt: "What's your current filming setup like?",
        required: true,
        options: [
            'Pretty solid — ring light and decent mic',
            'Just my phone, nothing fancy',
            'I have no idea — need help with this',
        ],
        skipWhen: { questionId: 'Q5.5', equals: [OFF_CAMERA_ANSWER] },
    },
    {
        id: 'Q5.8',
        section: 5,
        inputType: 'text',
        fieldKey: 'contentOffLimits',
        prompt: 'Are there any topics, angles, or content styles that are completely off-limits for your brand — anything your Doer must never publish?',
        // Required, but "'None' acceptable" — so no length floor.
        required: true,
    },
];
// ── Section 6 — Working style (2 questions, ~3 min) ──────────────────
/**
 * Q6.2's three regions. A named tuple rather than an inline array so the
 * shift-hours map below is keyed by the same values the question offers —
 * renaming a region breaks the map at compile time instead of silently
 * resolving to `undefined` in front of a founder.
 */
export const INTAKE_REGIONS = ['US / Canada', 'UK / Europe', 'Australia / NZ'];
/**
 * The Doer shift a region implies, as the founder should hear it.
 *
 * Q6.2 is the one question whose answer the agent must state back as a
 * consequence — "your Doer works 8:00am-4:00pm PST" — so the mapping has to
 * live somewhere both the control and the agent's tool result read from.
 *
 * The hours are `WORKING_HOURS_PRESETS` (working-hours.ts) restated in the
 * founder's terms: night → US/Canada, mid → UK/Europe, morning → Australia/NZ.
 * Those presets stay the operational truth — a doer's actual schedule is built
 * from them — and this is display copy derived from the same three shifts, which
 * is why it is a string and not a `WorkingHours`.
 */
export const REGION_DOER_SHIFT_HOURS = {
    'US / Canada': '8:00am-4:00pm PST',
    'UK / Europe': '8:00am-5:00pm GMT',
    'Australia / NZ': '10:00am-6:00pm AEST',
};
/**
 * The IANA zone each region is evaluated in when we need a real date boundary
 * — today only step 10's launch day.
 *
 * `REGION_DOER_SHIFT_HOURS` above cannot serve this: its values are display
 * copy ("8:00am-4:00pm PST"), and the abbreviations in them are not zones —
 * PST is ambiguous across the year, and "AEST" is written on a region that also
 * contains New Zealand. These are deliberately the most populous zone in each
 * region rather than an attempt at precision the three-way split cannot support;
 * the worst error is a few hours on a date that changes at midnight.
 */
export const REGION_TIME_ZONES = {
    'US / Canada': 'America/Los_Angeles',
    'UK / Europe': 'Europe/London',
    'Australia / NZ': 'Australia/Sydney',
};
export function isIntakeRegion(value) {
    return typeof value === 'string' && INTAKE_REGIONS.includes(value);
}
/** The IANA zone a region implies, or null when the region is unrecognised. */
export function timeZoneForRegion(region) {
    return isIntakeRegion(region) ? REGION_TIME_ZONES[region] : null;
}
/** The shift hours a region implies, or null when the region is unrecognised. */
export function doerShiftHoursForRegion(region) {
    return isIntakeRegion(region) ? REGION_DOER_SHIFT_HOURS[region] : null;
}
const SECTION_6 = [
    {
        id: 'Q6.1',
        section: 6,
        inputType: 'voice',
        fieldKey: 'autonomyPref',
        prompt: 'Your primary communication channel with your Doer will be the Doneverse dashboard. With that in mind: how much autonomy do you want your Doer to have — where do you want to be in the loop, and where do you trust them to just get on with it?',
        minWords: 30,
        required: true,
        coachingNudge: 'How much do you want them checking in? Some founders want to approve everything; others just want to see the finished result.',
    },
    {
        id: 'Q6.2',
        section: 6,
        inputType: 'region',
        fieldKey: 'region',
        prompt: "Where are you primarily based? The region you select sets your Doer's standard shift time — this is when they'll be working in your business every day.",
        required: true,
        options: INTAKE_REGIONS,
    },
];
// ── Section 7 — AI readiness (4 questions, ~3 min) ───────────────────
const SECTION_7 = [
    {
        id: 'Q7.1',
        section: 7,
        inputType: 'select',
        fieldKey: 'aiComfortLevel',
        prompt: 'How would you describe your current relationship with AI tools in your business?',
        required: true,
        options: [
            NOT_USING_AI_ANSWER,
            'Experimenting',
            'Using some tools actively',
            'Building with AI already',
        ],
    },
    {
        id: 'Q7.2',
        section: 7,
        inputType: 'text',
        fieldKey: 'aiToolsUsed',
        prompt: 'Which AI tools are you currently using? List everything — even tools you only use occasionally.',
        required: true,
        placeholder: 'ChatGPT, Claude, Midjourney, ElevenLabs, Zapier, Make, Perplexity...',
        skipWhen: { questionId: 'Q7.1', equals: [NOT_USING_AI_ANSWER] },
    },
    {
        id: 'Q7.3',
        section: 7,
        inputType: 'select',
        fieldKey: 'aiCloneOptin',
        prompt: "We're building AI voice clones — trained on how you speak and write so your Doer can generate content in your exact voice at scale. Would you be open to this?",
        required: true,
        options: [
            'Yes — fully in',
            'Yes — want to understand it first',
            'Not sure yet',
            'Not right now',
        ],
    },
    {
        id: 'Q7.4',
        section: 7,
        inputType: 'voice',
        fieldKey: 'automationInterest',
        prompt: "Are there any parts of your business you'd love to automate — things that feel repetitive, slow, or manual right now?",
        minWords: 10,
        required: true,
    },
];
/** All 41 active questions, in conversation order. */
export const INTAKE_QUESTIONS = [
    ...SECTION_1,
    ...SECTION_2,
    ...SECTION_3,
    ...SECTION_4,
    ...SECTION_5,
    ...SECTION_6,
    ...SECTION_7,
];
/** The spec's own count — a mismatch means a question was lost in editing. */
/** 41 from spec v3.0, plus Q1.0 (business name) and Q1.8a (revenue). */
export const INTAKE_QUESTION_COUNT = 43;
/** Brief Object version stamped on everything this registry produces. */
export const INTAKE_VERSION = 3;
// ── Training stacks (Q4.3 options, selected by Q4.2) ─────────────────
/**
 * The spec's two tool-stack tables. `STACKS` in the prototype had commented
 * slots for future industries; here an industry simply maps to one of these
 * keys, so adding a stack is adding one entry rather than editing a renderer.
 */
export const TRAINING_STACKS = {
    VMA: {
        'Email marketing': ['ActiveCampaign', 'Mailchimp', 'GHL', 'Kajabi'],
        'Landing pages / funnels': ['GHL', 'ClickFunnels', 'Kajabi'],
        'Appointment scheduling': ['Calendly', 'Gorgias', 'Help Scout'],
        'Admin tools': ['ActiveCampaign', 'Gorgias', 'Help Scout'],
        Design: ['Canva'],
        'Video editing': ['Descript', 'CapCut'],
        Website: ['WordPress', 'GHL', 'SquareSpace', 'Wix'],
        'Course portal': ['GHL', 'Kajabi', 'WordPress/Learndash', 'Teachable'],
        SEO: ['WordPress', 'SquareSpace', 'Wix'],
    },
    eCommerce: {
        'Email marketing': ['ActiveCampaign', 'Mailchimp', 'GHL', 'Kajabi'],
        'eCommerce store': ['Shopify'],
        'Admin tools': ['ActiveCampaign', 'Gorgias', 'Help Scout'],
        Design: ['Canva'],
        'Customer support': ['Gorgias'],
        'Inventory management': ['Shopify'],
        Shipping: ['Shopify'],
    },
};
/**
 * Industry → training track, verbatim from Q4.2's branch logic. Everything
 * that is not eCommerce trains on the VMA stack, including the undetected
 * case, so an unknown industry degrades to the broader stack rather than to
 * nothing.
 *
 * NOTE: this is the TRAINING track only. It is not the `founder_categories`
 * A/C/E/L/M/P/R mapping, which remains product decision #2d.
 */
/**
 * The industries Q4.2 can resolve to, in the order its selector offers them.
 *
 * Q4.2 carries no `options` of its own — detection proposes one of these and the
 * founder either confirms it or picks from the full list — so this IS that list,
 * and the training-track map below is keyed by it so the two cannot drift.
 */
export const INTAKE_INDUSTRIES = [
    'Coaching & Consulting',
    'eCommerce',
    'Real Estate',
    'Marketing Agency',
    'MedSpa / Allied Health',
    'Home Services / Trades',
    'Professional Services',
];
export const INDUSTRY_TRAINING_TRACK = {
    eCommerce: 'eCommerce',
    'Coaching & Consulting': 'VMA',
    'Real Estate': 'VMA',
    'Marketing Agency': 'VMA',
    'MedSpa / Allied Health': 'VMA',
    'Home Services / Trades': 'VMA',
    'Professional Services': 'VMA',
};
/**
 * Input types whose answer the BROWSER collects and persists itself, rather
 * than handing back to the agent as a tool result.
 *
 * These are exactly the questions no client tool can wait on: ElevenLabs caps a
 * client tool's response at 120s, and a founder hunting for a logo (`upload`) or
 * filling a multi-row grid (`structured`) will take longer. Their controls open
 * and return nothing, so the answer needs a door into the API that is not the
 * agent's MCP surface.
 *
 * Everything else — voice, text, select, url, industry, region — already has a
 * working path: the answer travels back through the agent, which records it.
 * Deriving this from input type rather than listing question ids means adding a
 * fourth structured question needs no change here, and — more to the point —
 * that the browser cannot write an answer to a question the agent already owns.
 */
export const BROWSER_COLLECTED_INPUT_TYPES = ['upload', 'structured'];
/** Whether the browser may persist this question's answer directly. */
export function isBrowserCollectedQuestion(question) {
    return BROWSER_COLLECTED_INPUT_TYPES.includes(question.inputType);
}
export function isIntakeIndustry(value) {
    return typeof value === 'string' && INTAKE_INDUSTRIES.includes(value);
}
/**
 * The stack a detected industry trains on. Unknown → VMA, per the spec — which
 * covers a founder who typed their own industry rather than picking one, so the
 * lookup must stay tolerant of any string.
 */
export function trainingTrackForIndustry(industry) {
    if (!isIntakeIndustry(industry))
        return 'VMA';
    return INDUSTRY_TRAINING_TRACK[industry];
}
// ── Lookup + skip logic ──────────────────────────────────────────────
const BY_ID = new Map(INTAKE_QUESTIONS.map((question) => [question.id, question]));
export function getIntakeQuestion(id) {
    return BY_ID.get(id);
}
// ── The canonical shape of an answer ─────────────────────────────────
/**
 * Reshape one answer into the form this question's answer is DEFINED to have.
 *
 * The canonical shape of a `select` answer is fixed by the QUESTION, never by
 * the value that arrived:
 *
 *   - single-select (`multiSelect` falsy) → the option **string**
 *   - multi-select (`multiSelect: true`) → an **array** of option strings
 *
 * Every other input type is stored exactly as it arrives; this function is not
 * a general coercion layer.
 *
 * It exists because the control that collects a select answer cannot express
 * that distinction. `ui_collect_select` returns `{"selected": [...]}` — always
 * an array, deliberately, even for a single-select question — and the agent
 * records what it was handed. Left alone, Q7.1 lands as
 * `["Not using it yet"]`, which is not `=== NOT_USING_AI_ANSWER`, so every
 * `skipWhen` rule silently stops firing and the finalized Brief Object carries
 * a one-element array into consumers that compare against the option string
 * (`isAiReady`, `deriveFounderCategory`, the brief form's select fields).
 *
 * Applied on the way IN, so storage holds one shape, and again on the way OUT,
 * so answers captured before this existed read the same as new ones.
 *
 * What it deliberately does NOT collapse: an empty array (an unanswered
 * question — `validateIntakeAnswer` must still report `required`) and an array
 * of two or more on a single-select (a real error it must still report as
 * `invalid`). Silencing either would turn a loud problem into a quiet one.
 */
export function normalizeIntakeAnswer(question, value) {
    if (!question || question.inputType !== 'select')
        return value;
    if (question.multiSelect) {
        // The mirror case: one option is still a list of one on a multi-select, so
        // every consumer can assume an array without checking.
        return typeof value === 'string' ? [value] : value;
    }
    if (Array.isArray(value) && value.length === 1 && typeof value[0] === 'string') {
        return value[0];
    }
    return value;
}
/**
 * Should this question be hidden, given the answers so far?
 *
 * Evaluated AFTER the controlling answer is saved — an unanswered controller
 * cannot skip anything, because "not yet answered" and "answered with the
 * skipping value" are different states and only the second one may hide a
 * question. Skipped questions are never shown to the founder, so a question
 * hidden in error is silently lost rather than visibly wrong.
 *
 * The comparison runs on the CANONICAL value, not the raw one. All three skip
 * rules are controlled by a single-select, whose collecting tool hands back a
 * one-element array — so comparing what arrived made every conditional skip
 * dead, and reading through `normalizeIntakeAnswer` is what makes answers
 * already stored in that shape skip correctly too.
 */
export function shouldSkip(question, answers) {
    const rule = question.skipWhen;
    if (!rule)
        return false;
    const controlling = normalizeIntakeAnswer(getIntakeQuestion(rule.questionId), answers[rule.questionId]);
    if (typeof controlling !== 'string')
        return false;
    return rule.equals.includes(controlling);
}
/** The questions actually presented, in order, for a given set of answers. */
export function visibleIntakeQuestions(answers) {
    return INTAKE_QUESTIONS.filter((question) => !shouldSkip(question, answers));
}
/**
 * The next question to ask — the first visible one with no answer.
 *
 * This is what "resume from the last completed question" means in practice:
 * derived from the answers rather than from a stored cursor, so a session that
 * crashed mid-write cannot resume pointing at a question the founder already
 * answered, and a late skip re-derives correctly.
 */
export function nextIntakeQuestion(answers) {
    return (visibleIntakeQuestions(answers).find((question) => {
        const answer = answers[question.id];
        return answer === undefined || answer === null || answer === '';
    }) ?? null);
}
// ── Answer validation ────────────────────────────────────────────────
/** Words, counted the way a founder would: runs of non-whitespace. */
export function countWords(text) {
    return text.trim().split(/\s+/).filter(Boolean).length;
}
/** One service row from Q1.6 — price is optional ("if you can"). */
export const ServiceRowSchema = z.object({
    name: z.string().min(1),
    price: z.string().optional(),
});
/** Q2.3 — at least one side must be filled. */
export const BrandVoiceSchema = z
    .object({ always: z.string().optional(), never: z.string().optional() })
    .refine((value) => Boolean(value.always?.trim() || value.never?.trim()), {
    message: 'Fill in at least one of "always" or "never"',
});
/**
 * Q1.9 — website required and URL-validated; socials optional.
 *
 * Every field here lands on the Brief Object, which the founder's DOER reads and
 * the dashboard renders as links. So the scheme is guarded server-side: a bare
 * `.url()` accepts `javascript:` and `data:`, and the five social fields
 * previously accepted any string at all (#835). See `web-urls.ts`.
 *
 * The socials stay looser than the website ON PURPOSE — the spec asks only for
 * "Website (required, URL validated)", and founders type `@doneverse` as often
 * as a profile URL. `handleOrWebUrlSchema` keeps handles working while refusing
 * a foreign scheme.
 */
export const SocialLinksSchema = z.object({
    website: webUrlSchema,
    instagram: handleOrWebUrlSchema.optional(),
    facebook: handleOrWebUrlSchema.optional(),
    linkedin: handleOrWebUrlSchema.optional(),
    youtube: handleOrWebUrlSchema.optional(),
    other: handleOrWebUrlSchema.optional(),
});
/**
 * Validate one answer against its question.
 *
 * Returns a problem or null. A voice answer is validated against its
 * TRANSCRIPT — the spec is explicit that the word floor runs on the
 * transcription, not on the audio, so a caller passes text here either way.
 */
export function validateIntakeAnswer(question, answer) {
    const problem = (code, message) => ({ questionId: question.id, code, message });
    const isEmpty = answer === undefined ||
        answer === null ||
        (typeof answer === 'string' && answer.trim() === '') ||
        (Array.isArray(answer) && answer.length === 0);
    if (isEmpty) {
        return question.required ? problem('required', 'This question needs an answer') : null;
    }
    switch (question.inputType) {
        case 'select': {
            const values = Array.isArray(answer) ? answer : [answer];
            if (!question.multiSelect && values.length > 1) {
                return problem('invalid', 'Only one option may be selected');
            }
            if (question.options) {
                const unknown = values.find((value) => typeof value !== 'string' || !question.options.includes(value));
                if (unknown !== undefined) {
                    return problem('not_an_option', `"${String(unknown)}" is not one of the options`);
                }
            }
            return null;
        }
        case 'structured': {
            if (question.id === 'Q1.6') {
                const rows = z.array(ServiceRowSchema).min(1).safeParse(answer);
                return rows.success ? null : problem('invalid', 'Add at least one service with a name');
            }
            if (question.id === 'Q2.3') {
                const voice = BrandVoiceSchema.safeParse(answer);
                return voice.success ? null : problem('invalid', voice.error.issues[0].message);
            }
            return null;
        }
        case 'url': {
            const links = SocialLinksSchema.safeParse(answer);
            return links.success ? null : problem('invalid', 'A valid website URL is required');
        }
        case 'upload':
            return Array.isArray(answer) ? null : problem('invalid', 'Expected a list of files');
        // voice / text / industry / region all carry text.
        default: {
            if (typeof answer !== 'string')
                return problem('invalid', 'Expected text');
            if (question.minWords && countWords(answer) < question.minWords) {
                return problem('under_threshold', question.coachingNudge ?? `Give me at least ${question.minWords} words`);
            }
            if (question.minChars && answer.trim().length < question.minChars) {
                return problem('under_threshold', `Give me at least ${question.minChars} characters`);
            }
            return null;
        }
    }
}
//# sourceMappingURL=intake-questions.js.map